"use client";

import { Component, ErrorInfo, ReactNode } from "react";
// import { fetchWithXSRF } from "@/lib/fetchWithXSRF";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

/**
 * ErrorBoundary component catches JavaScript errors in child components.
 * It displays a fallback UI and prevents the entire app from crashing.
 */
class ErrorBoundary extends Component<Props, State> {
  retryCount = 0;
  maxRetries = 3;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Always set hasError, but error boundary will decide to retry or not
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Retry logic: try to re-render up to maxRetries times before showing fallback
    if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      this.setState({
        hasError: false,
        error: undefined,
        errorInfo: undefined,
      });
      return;
    }
    // Log the error to console in development
    if (process.env.NODE_ENV === "development") {
      console.error("ErrorBoundary caught an error:", error, errorInfo);
    }

    // Log error to a remote service in production
    if (process.env.NODE_ENV === "production") {
      try {
        // Use fetchWithXSRF so logging calls include credentials and XSRF header
        fetch("/api/log-client-error", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            error: error.toString(),
            errorInfo: errorInfo.componentStack,
            url: window.location.href,
            userAgent: navigator.userAgent,
            time: new Date().toISOString(),
          }),
        }).catch(() => {
          /* ignore logging errors */
        });
      } catch (e) {
        // Ignore logging errors
      }
    }

    // Call the onError callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    this.setState({
      error,
      errorInfo,
    });
  }

  render() {
    if (this.state.hasError) {
      // If retryCount < maxRetries, don't show fallback, just try again
      if (this.retryCount < this.maxRetries) {
        return null; // Will trigger another render attempt
      }
      // In production, render nothing on error
      if (process.env.NODE_ENV === "production") {
        return null;
      }
      // Render custom fallback UI if provided (development only)
      if (this.props.fallback) {
        return this.props.fallback;
      }
      // Default fallback UI (development only)
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="max-w-md mx-auto text-center bg-card rounded-xl shadow-sm p-8">
            <div className="mb-4">
              <svg
                className="mx-auto h-10 w-12 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-4">
              Something went wrong
            </h1>
            <p className="text-muted-foreground mb-6">
              We apologize for the inconvenience. Please try refreshing the page
              or contact support if the problem persists.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => {
                  this.retryCount = 0;
                  this.setState({ hasError: false });
                }}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-primary text-primary-foreground py-2 px-4 rounded-md hover:bg-primary/90 transition-colors"
              >
                Refresh Page
              </button>
            </div>
            {process.env.NODE_ENV === "development" && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm text-muted-foreground">
                  Error Details (Development Only)
                </summary>
                <pre className="mt-2 text-xs text-red-600 bg-red-50 dark:bg-black dark:text-red-400 p-3 rounded overflow-auto">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack
                    ? typeof this.state.errorInfo.componentStack === "string"
                      ? this.state.errorInfo.componentStack
                      : JSON.stringify(
                          this.state.errorInfo.componentStack,
                          null,
                          2,
                        )
                    : ""}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
